import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Clock, Sparkles, Heart, ArrowRight, Image } from "lucide-react";
import { COUPLE, HERO_IMAGE } from "@/lib/constants";
import Countdown from "@/components/Countdown";
import ScratchCard from "@/components/ScratchCard";
import Reveal from "@/components/Reveal";
import RSVPForm from "@/components/RSVPForm";
import MessageWall from "@/components/MessageWall";
import { useReveal } from "@/contexts/RevealContext";

const details = [
  { icon: Calendar, label: "Date", value: COUPLE.weddingDateLabel },
  { icon: Sparkles, label: "Dhaare Muhurta", value: COUPLE.muhurta },
  { icon: Clock, label: "Reception", value: COUPLE.reception },
  { icon: MapPin, label: "Venue", value: COUPLE.venue },
];

export default function Home() {
  const { revealed } = useReveal();
  const revealedRegionRef = useRef(null);

  // Smoothly scroll to the reveal region the first time it appears
  useEffect(() => {
    if (revealed && revealedRegionRef.current) {
      const t = setTimeout(() => {
        revealedRegionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [revealed]);

  return (
    <div data-testid="home-page" className="relative">
      {/* HERO — names + hashtag + scratch card (NO date, NO countdown before reveal) */}
      <section className="relative min-h-screen flex flex-col items-center justify-start px-5 pt-28 pb-20 text-center overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 opacity-50"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "saturate(0.85) brightness(0.55)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-b from-ink-900/60 via-ink-900/40 to-ink-900"
        />

        <div className="animate-fade-in mt-6 md:mt-10">
          <p className="text-[0.65rem] md:text-xs uppercase tracking-[0.5em] text-gold mb-6">
            We are getting married
          </p>
        </div>

        <h1
          data-testid="hero-title"
          className="font-serif font-light tracking-tight text-ivory leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl animate-fade-up"
        >
          {COUPLE.groom}
          <span className="block my-2 md:my-3">
            <span className="inline-block font-script text-gold-light text-3xl md:text-5xl align-middle mx-2">
              &amp;
            </span>
          </span>
          <span className="italic gold-text">{COUPLE.bride}</span>
        </h1>

        <p
          className="mt-6 font-serif italic text-xl md:text-2xl text-ivory-muted animate-fade-up"
          style={{ animationDelay: "180ms" }}
        >
          Our Journey Together
        </p>

        <p
          data-testid="hero-hashtag"
          className="mt-3 font-serif italic text-3xl md:text-5xl hashtag-glow animate-fade-up"
          style={{ animationDelay: "240ms" }}
        >
          #PandA
        </p>

        <div
          className="mt-3 ornament-divider w-72 mx-auto animate-fade-up"
          style={{ animationDelay: "300ms" }}
        >
          <Heart size={12} className="text-gold/80" />
        </div>

        {/* Prompt text replaces the date until revealed */}
        {!revealed && (
          <p
            data-testid="reveal-prompt"
            className="mt-6 font-serif italic text-base md:text-lg text-ivory-muted max-w-md animate-fade-up"
            style={{ animationDelay: "360ms" }}
          >
            A secret waits beneath the veil — scratch below to uncover the day ✨
          </p>
        )}
        {revealed && (
          <p
            data-testid="hero-date-label"
            className="mt-5 text-base md:text-lg uppercase tracking-[0.4em] text-ivory-dim animate-fade-up"
          >
            {COUPLE.weddingDateLabel}
          </p>
        )}

        {/* SCRATCH CARD — immediately below names, before any date/countdown */}
        <div
          data-testid="hero-scratch-wrap"
          className="mt-12 md:mt-14 w-full max-w-lg animate-fade-up"
          style={{ animationDelay: "480ms" }}
        >
          <ScratchCard />
          {!revealed && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <span className="text-xs text-ivory-dim italic">
                ✦ Uncover the magic before the date appears
              </span>
            </div>
          )}
        </div>
      </section>

      {/* POST-REVEAL REGION — countdown + save the date + RSVP + wishes */}
      {revealed && (
        <div
          ref={revealedRegionRef}
          data-testid="post-reveal-region"
          className="animate-fade-up"
          style={{ animationDuration: "1s" }}
        >
          {/* COUNTDOWN */}
          <section
            id="countdown"
            className="relative px-5 md:px-10 py-20 md:py-28"
            data-testid="countdown-section"
          >
            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-[0.65rem] uppercase tracking-[0.5em] text-gold mb-3">
                Counting every heartbeat
              </p>
              <h2 className="font-serif font-light text-4xl md:text-6xl text-ivory">
                <span className="italic gold-text">{COUPLE.weddingDateLabel}</span>
              </h2>
              <p className="mt-4 font-serif italic text-lg md:text-xl text-ivory-muted">
                Until we say forever…
              </p>
            </div>
            <Countdown />
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            </div>
          </section>

          {/* DETAILS / SAVE THE DATE */}
          <section
            id="details"
            className="relative px-5 md:px-10 py-20 md:py-28"
            data-testid="details-section"
          >
            <Reveal className="text-center mb-14">
              <p className="text-[0.65rem] uppercase tracking-[0.5em] text-gold mb-3">
                The Celebration
              </p>
              <h2 className="font-serif font-light text-4xl md:text-6xl text-ivory">
                Save the <span className="italic gold-text">Date</span>
              </h2>
              <div className="ornament-divider w-72 mx-auto mt-6">
                <span className="font-serif italic text-gold text-sm">#PandA</span>
              </div>
            </Reveal>

            <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {details.map((d, i) => (
                <Reveal key={d.label} delay={i * 100}>
                  <div
                    data-testid={`detail-card-${d.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    className="glass-card relative h-full p-7 md:p-8 group"
                  >
                    <span className="absolute top-3 right-3 text-[0.55rem] uppercase tracking-[0.32em] text-gold/60">
                      #PandA
                    </span>
                    <div className="w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold mb-5 group-hover:bg-burgundy/40 group-hover:border-gold transition">
                      <d.icon size={18} />
                    </div>
                    <p className="text-[0.6rem] uppercase tracking-[0.4em] text-ivory-dim">
                      {d.label}
                    </p>
                    <p className="mt-2 font-serif text-2xl md:text-3xl text-ivory leading-snug">
                      {d.value}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={200} className="mt-10 text-center">
              <a
                href={COUPLE.locationUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="view-location-button"
                className="btn-gold"
              >
                <MapPin size={14} /> View Location
              </a>
            </Reveal>
          </section>

          {/* RSVP */}
          <section
            id="rsvp"
            className="relative px-5 md:px-10 py-20 md:py-24"
            data-testid="rsvp-section"
          >
            <Reveal className="text-center max-w-2xl mx-auto mb-12">
              <p className="text-[0.65rem] uppercase tracking-[0.5em] text-gold mb-3">
                Kindly respond
              </p>
              <h2 className="font-serif font-light text-4xl md:text-6xl text-ivory">
                <span className="italic gold-text">RSVP</span>
              </h2>
              <p className="mt-5 font-serif italic text-lg text-ivory-muted">
                Your presence turns a day into a memory. Let us know you’re coming ❤️
              </p>
            </Reveal>
            <div className="max-w-3xl mx-auto">
              <Reveal>
                <RSVPForm />
              </Reveal>
            </div>
          </section>

          {/* WISHES */}
          <section
            id="wishes"
            className="relative px-5 md:px-10 py-20 md:py-24"
            data-testid="wishes-section"
          >
            <Reveal className="text-center max-w-2xl mx-auto mb-12">
              <p className="text-[0.65rem] uppercase tracking-[0.5em] text-gold mb-3">
                Guest Wishes
              </p>
              <h2 className="font-serif font-light text-4xl md:text-6xl text-ivory">
                Leave a note for <span className="italic gold-text">#PandA</span>
              </h2>
              <p className="mt-5 font-serif italic text-lg text-ivory-muted">
                Blessings, memories, little poems — anything your heart wants to say.
              </p>
            </Reveal>
            <div className="max-w-6xl mx-auto">
              <Reveal>
                <MessageWall />
              </Reveal>
            </div>
          </section>
        </div>
      )}

      {/* Gentle nudge when not yet revealed (extra breathing room before footer) */}
      {!revealed && (
        <section
          className="relative px-5 py-16 text-center"
          data-testid="pre-reveal-outro"
        >
          <p className="font-serif italic text-xl md:text-2xl text-ivory-muted">
            The date, the time, and a tiny secret — all beneath the veil above.
          </p>
          <p className="mt-3 font-serif italic text-2xl hashtag-glow">#PandA</p>
        </section>
      )}
    </div>
  );
}
